import React from 'react';

export function StatCard({ label, value, sub, accent = 'neon', icon: Icon, testId }) {
  const accents = {
    neon: 'text-vj-neon', gold: 'text-vj-gold', silver: 'text-foreground',
    red: 'text-vj-red', emerald: 'text-vj-emerald',
  };
  return (
    <div className="vj-card p-5 h-full flex flex-col justify-between" data-testid={testId}>
      <div className="flex items-center justify-between">
        <span className="text-xs uppercase tracking-wider text-vj-grey">{label}</span>
        {Icon && <Icon size={16} className={accents[accent]} />}
      </div>
      <div className="mt-3">
        <div className={`font-mono text-2xl font-semibold ${accents[accent]}`}>{value}</div>
        {sub && <div className="text-xs text-vj-grey mt-1">{sub}</div>}
      </div>
    </div>
  );
}

const STATUS_STYLES = {
  green: 'bg-vj-neon/15 text-vj-neon border-vj-neon/30',
  gold: 'bg-vj-gold/15 text-vj-gold border-vj-gold/30',
  red: 'bg-vj-red/15 text-vj-red border-vj-red/30',
  grey: 'bg-white/5 text-vj-grey border-border',
  blue: 'bg-sky-400/15 text-sky-300 border-sky-400/30',
};

export function Badge({ children, tone = 'grey', testId }) {
  return (
    <span data-testid={testId} className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${STATUS_STYLES[tone] || STATUS_STYLES.grey}`}>
      {children}
    </span>
  );
}

const STATUS_TONE = {
  credited: 'green', blockchain_verified: 'blue', confirming: 'gold', awaiting_payment: 'grey',
  pending_review: 'gold', rejected: 'red', needs_investigation: 'red',
  completed: 'green', approved: 'blue', processing: 'gold', broadcast: 'blue',
  pending_admin_approval: 'gold', pending_settlement: 'gold', failed: 'red', submitted: 'grey',
  posted: 'green', awaiting_attribution: 'gold', unattributed: 'gold', healthy: 'green', paused: 'gold', error: 'red',
};

export function StatusBadge({ status, testId }) {
  const label = String(status || '').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
  return <Badge tone={STATUS_TONE[status] || 'grey'} testId={testId}>{label}</Badge>;
}

export function SectionTitle({ eyebrow, title, desc, center }) {
  return (
    <div className={`${center ? 'text-center mx-auto' : ''} max-w-2xl mb-10`}>
      {eyebrow && <div className="text-xs font-semibold tracking-[0.2em] uppercase text-vj-neon mb-3">{eyebrow}</div>}
      <h2 className="font-display text-3xl lg:text-4xl font-bold text-foreground tracking-tight">{title}</h2>
      {desc && <p className="mt-4 text-vj-grey">{desc}</p>}
    </div>
  );
}

export function Panel({ title, action, children, className = '', testId }) {
  return (
    <div className={`vj-card p-5 lg:p-6 ${className}`} data-testid={testId}>
      {(title || action) && (
        <div className="flex items-center justify-between mb-4">
          {title && <h3 className="font-display text-sm font-semibold tracking-wide text-foreground">{title}</h3>}
          {action}
        </div>
      )}
      {children}
    </div>
  );
}

export function Empty({ children = 'No records yet.' }) {
  return <div className="py-10 text-center text-sm text-vj-grey">{children}</div>;
}

export function Disclaimer({ children }) {
  return (
    <div className="rounded-lg border border-vj-gold/30 bg-vj-gold/5 px-4 py-3 text-xs text-vj-gold/90">
      {children}
    </div>
  );
}
